/**
 * Rung 1 of the fallback ladder — the SERVER asset (SPEC_AUDIO_GUIDANCE §3.1, SPEC_CAPTURE_UX_R2 §3.4 #1).
 *
 * THE BE DOES NOT SEND THIS YET. `script[].audio` is a PROPOSED contract addition (v0.1.13 §4) and no publish
 * job exists — so this module is a SEAM, built and tested against a fake asset, never against a live route. It
 * is here so that the day the be starts sending refs the client already verifies them; it is not evidence that
 * spoken assets exist.
 *
 * What it does, in order:
 *   1. picks the encoding the device can actually decode (`canPlayType`: Opus in Ogg first, AAC in M4A second) —
 *      a device that can decode neither falls through to rung 2, never to a silent "playing" state;
 *   2. fetches the bytes on the case BEARER (in memory, never in a URL — the index.html rule);
 *   3. verifies the declared SHA-256 with WebCrypto before anything is played — a mismatch is a typed REFUSAL,
 *      never a wrong clip (SPEC_AUDIO_GUIDANCE §3.3 "a mismatch is a refusal");
 *   4. hands back an object URL plus its `revoke()` — the caller revokes; nothing leaks across steps.
 */

import { CAPTURE_BASE, getBearer } from '@/api/client';
import { sha256Hex } from '@/capture/chainHash';

/**
 * The seam's wire shape: `script[].audio: {<lang>: {url, sha256, enc}}`.
 *
 * Tolerant of the richer PROPOSED `AudioRef` (contract v0.1.13 §4: `{assetId, sha256Opus, sha256Aac, …}`) so the
 * client does not need a second parser the day the contract lands: `assetId` builds the guidance route, and the
 * per-encoding digests are read when the flat `sha256` is absent. Unknown fields are ignored.
 */
export interface GuidanceAudioRef {
  readonly url?: string | null;
  readonly assetId?: string | null;
  readonly sha256?: string | null;
  /** `opus` | `aac`, or a full mime type. Absent ⇒ both encodings are offered and `canPlayType` decides. */
  readonly enc?: string | null;
  readonly sha256Opus?: string | null;
  readonly sha256Aac?: string | null;
  readonly durationMs?: number | null;
  /** Notices are an accessibility aid, never the consent record (SPEC_AUDIO_GUIDANCE §2.6). */
  readonly aidOnly?: boolean | null;
}

export type Encoding = 'opus' | 'aac';

export const OPUS_MIME = 'audio/ogg; codecs=opus';
export const AAC_MIME = 'audio/mp4; codecs="mp4a.40.2"';

export const ENCODING_MIME: Readonly<Record<Encoding, string>> = { opus: OPUS_MIME, aac: AAC_MIME };

export interface PickedEncoding {
  readonly enc: Encoding;
  readonly mime: string;
  readonly sha256: string;
}

/** `canPlayType` answers '', 'maybe' or 'probably' — anything non-empty counts, as the platform intends. */
export type CanPlayType = (mime: string) => string;

function declaredSha(ref: GuidanceAudioRef, enc: Encoding): string | null {
  const flat = typeof ref.sha256 === 'string' && ref.sha256.trim() !== '' ? ref.sha256.trim() : null;
  const perEnc = enc === 'opus' ? ref.sha256Opus : ref.sha256Aac;
  const scoped = typeof perEnc === 'string' && perEnc.trim() !== '' ? perEnc.trim() : null;
  return scoped ?? flat;
}

/**
 * Which encoding to ask for. The ref's own `enc` narrows the choice (a single-format asset); otherwise Opus is
 * preferred (a third of the bytes at the same intelligibility) with AAC for older WebViews and iOS < 18.4.
 * `null` ⇒ the device can decode nothing we hold, or the ref carries no digest for what it can decode.
 */
export function pickEncoding(ref: GuidanceAudioRef, canPlayType: CanPlayType): PickedEncoding | null {
  const declared = typeof ref.enc === 'string' ? ref.enc.toLowerCase() : '';
  const order: Encoding[] = declared.includes('opus') || declared.includes('ogg')
    ? ['opus']
    : declared.includes('aac') || declared.includes('mp4') || declared.includes('m4a')
      ? ['aac']
      : ['opus', 'aac'];
  for (const enc of order) {
    const mime = ENCODING_MIME[enc];
    if (canPlayType(mime).trim() === '') continue;
    const sha256 = declaredSha(ref, enc);
    if (!sha256) continue;
    return { enc, mime, sha256 };
  }
  return null;
}

export interface LoadedAsset {
  readonly ok: true;
  readonly objectUrl: string;
  readonly mime: string;
  readonly enc: Encoding;
  readonly bytes: number;
  readonly sha256: string;
  readonly revoke: () => void;
}
export type AssetRefusal =
  | { readonly ok: false; readonly reason: 'NO_PLAYABLE_ENCODING' }
  | { readonly ok: false; readonly reason: 'NO_URL' }
  | { readonly ok: false; readonly reason: 'HTTP'; readonly status: number }
  | { readonly ok: false; readonly reason: 'UNREACHABLE' }
  | { readonly ok: false; readonly reason: 'SHA256_MISMATCH'; readonly expected: string; readonly actual: string };

export type AssetResult = LoadedAsset | AssetRefusal;

export interface FetchAssetOptions {
  readonly canPlayType?: CanPlayType;
  readonly fetchImpl?: typeof fetch;
  /** Injected in tests; defaults to the in-memory capture bearer. */
  readonly bearer?: string | null;
}

function probeCanPlayType(): CanPlayType {
  return (mime: string): string => {
    try {
      const el = document.createElement('audio');
      return typeof el.canPlayType === 'function' ? el.canPlayType(mime) : '';
    } catch {
      return '';
    }
  };
}

export function guidanceAssetUrl(ref: GuidanceAudioRef, enc: Encoding): string | null {
  if (typeof ref.url === 'string' && ref.url.trim() !== '') return ref.url.trim();
  if (typeof ref.assetId === 'string' && ref.assetId.trim() !== '') {
    return `${CAPTURE_BASE}/guidance/${encodeURIComponent(ref.assetId.trim())}?enc=${enc}`;
  }
  return null;
}

export async function fetchGuidanceAsset(ref: GuidanceAudioRef, opts: FetchAssetOptions = {}): Promise<AssetResult> {
  const picked = pickEncoding(ref, opts.canPlayType ?? probeCanPlayType());
  if (!picked) return { ok: false, reason: 'NO_PLAYABLE_ENCODING' };
  const url = guidanceAssetUrl(ref, picked.enc);
  if (!url) return { ok: false, reason: 'NO_URL' };
  const doFetch = opts.fetchImpl ?? (typeof fetch === 'function' ? fetch : null);
  if (!doFetch) return { ok: false, reason: 'UNREACHABLE' };
  const bearer = opts.bearer !== undefined ? opts.bearer : getBearer();
  let res: Response;
  try {
    res = await doFetch(url, {
      method: 'GET',
      headers: { Accept: picked.mime, ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}) },
    });
  } catch {
    return { ok: false, reason: 'UNREACHABLE' };
  }
  if (!res.ok) return { ok: false, reason: 'HTTP', status: res.status };
  let bytes: Uint8Array;
  try {
    bytes = new Uint8Array(await res.arrayBuffer());
  } catch {
    return { ok: false, reason: 'UNREACHABLE' };
  }
  const actual = await sha256Hex(bytes);
  if (actual.toLowerCase() !== picked.sha256.toLowerCase()) {
    return { ok: false, reason: 'SHA256_MISMATCH', expected: picked.sha256.toLowerCase(), actual };
  }
  const blob = new Blob([bytes as BlobPart], { type: picked.mime });
  const objectUrl = URL.createObjectURL(blob);
  let revoked = false;
  return {
    ok: true,
    objectUrl,
    mime: picked.mime,
    enc: picked.enc,
    bytes: bytes.byteLength,
    sha256: actual,
    revoke: () => {
      if (revoked) return;
      revoked = true;
      URL.revokeObjectURL(objectUrl);
    },
  };
}

/** The per-language refs a script shot may carry. `{}` / absent ⇒ no server asset for any language (today: always). */
export function audioRefFor(audio: unknown, locale: string): GuidanceAudioRef | null {
  if (!audio || typeof audio !== 'object') return null;
  const map = audio as Record<string, unknown>;
  const lang = locale.toLowerCase();
  const base = lang.split('-')[0] ?? lang;
  for (const key of Object.keys(map)) {
    const k = key.toLowerCase();
    if (k === lang || k === base || k.split('-')[0] === base) {
      const ref = map[key];
      if (ref && typeof ref === 'object') return ref as GuidanceAudioRef;
    }
  }
  return null;
}
